import type { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios'

type TokenRefreshInterceptorOptions = {
  refreshToken: () => Promise<string | undefined>
  onRefreshFailure: () => void
  shouldIntercept?: (error: AxiosError) => boolean
}

type RetryableConfig = { _retry?: boolean } & InternalAxiosRequestConfig

export const createTokenRefreshInterceptor = (
  axiosInstance: AxiosInstance,
  options: TokenRefreshInterceptorOptions,
): void => {
  let refreshPromise: Promise<string | undefined> | null = null
  let failureTriggered = false

  const {
    refreshToken,
    onRefreshFailure,
    shouldIntercept = (error) => error.response?.status === 401,
  } = options

  const triggerFailureOnce = () => {
    if (failureTriggered) {
      return
    }
    failureTriggered = true
    onRefreshFailure()
  }

  axiosInstance.interceptors.response.use(undefined, async (error: AxiosError) => {
    const originalConfig = error.config as RetryableConfig | undefined

    if (!originalConfig || !shouldIntercept(error)) {
      return Promise.reject(error)
    }

    if (originalConfig._retry) {
      triggerFailureOnce()
      return Promise.reject(error)
    }

    originalConfig._retry = true

    if (!refreshPromise) {
      refreshPromise = refreshToken().finally(() => {
        refreshPromise = null
      })
    }

    try {
      const newToken = await refreshPromise

      if (!newToken) {
        triggerFailureOnce()
        return Promise.reject(error)
      }

      originalConfig.headers.setAuthorization(`Bearer ${newToken}`)
      return axiosInstance(originalConfig)
    } catch {
      triggerFailureOnce()
      return Promise.reject(error)
    }
  })
}
